// Geração de ficheiros .xlsx no browser, sem dependências.
//
// Um .xlsx é um ZIP com XML lá dentro (SpreadsheetML). Escrevemos os dois à
// mão — o ZIP sem compressão (método "store"), o que dispensa qualquer
// biblioteca e mantém o bundle na mesma. Chega bem para listagens de algumas
// milhares de linhas, que é o caso dos relatórios do backoffice.
//
// Valores das células:
//   'texto'        → texto
//   { n: 12.5 }    → número com formato de moeda (soma e pivota no Excel)
//   { d: iso }     → data/hora     (dd/mm/aaaa hh:mm)
//   { d: iso, dia: true } → só data (dd/mm/aaaa)
//   '' / null      → célula vazia

const escXml = (s) => String(s).replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c]
))

// 0 → A, 25 → Z, 26 → AA…
function letraCol(n) {
  let s = ''
  n += 1
  while (n > 0) {
    const r = (n - 1) % 26
    s = String.fromCharCode(65 + r) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

// Serial de data do Excel: dias desde 1899-12-30, em hora local (as datas são
// mostradas ao utilizador na hora do salão, não em UTC).
function serialExcel(iso) {
  const d = new Date(iso)
  const ms = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds())
  return (ms - Date.UTC(1899, 11, 30)) / 86400000
}

// Índices em `cellXfs` (ver ESTILOS): 1 cabeçalho, 2 data, 3 data/hora, 4 moeda.
function celulaXml(v, ref) {
  if (v === '' || v == null) return ''
  if (typeof v === 'object' && 'd' in v) {
    if (!v.d) return ''
    return `<c r="${ref}" s="${v.dia ? 2 : 3}"><v>${serialExcel(v.d)}</v></c>`
  }
  if (typeof v === 'object' && 'n' in v) return `<c r="${ref}" s="4"><v>${Number(v.n) || 0}</v></c>`
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escXml(v)}</t></is></c>`
}

// `cols`: [{ t: 'Título', w: 18 }]
function folhaXml(cols, linhas) {
  const larguras = cols
    .map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${c.w || 16}" customWidth="1"/>`)
    .join('')

  const cabecalho = '<row r="1">' + cols
    .map((c, i) => `<c r="${letraCol(i)}1" s="1" t="inlineStr"><is><t>${escXml(c.t)}</t></is></c>`)
    .join('') + '</row>'

  const corpo = linhas.map((linha, li) => {
    const r = li + 2
    return `<row r="${r}">` + linha.map((v, ci) => celulaXml(v, letraCol(ci) + r)).join('') + '</row>'
  }).join('')

  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    // Cabeçalho fixo ao rolar.
    '<sheetViews><sheetView workbookViewId="0">' +
    '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
    '</sheetView></sheetViews>' +
    `<cols>${larguras}</cols>` +
    `<sheetData>${cabecalho}${corpo}</sheetData>` +
    `<autoFilter ref="A1:${letraCol(cols.length - 1)}${Math.max(1, linhas.length + 1)}"/>` +
    '</worksheet>'
}

const ESTILOS =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="3">' +
    '<numFmt numFmtId="164" formatCode="dd/mm/yyyy"/>' +
    '<numFmt numFmtId="165" formatCode="dd/mm/yyyy\\ hh:mm"/>' +
    '<numFmt numFmtId="166" formatCode="#,##0.00\\ &quot;€&quot;"/>' +
  '</numFmts>' +
  '<fonts count="2">' +
    '<font><sz val="11"/><name val="Calibri"/></font>' +
    '<font><b/><sz val="11"/><name val="Calibri"/></font>' +
  '</fonts>' +
  '<fills count="3">' +
    '<fill><patternFill patternType="none"/></fill>' +
    '<fill><patternFill patternType="gray125"/></fill>' +
    '<fill><patternFill patternType="solid"><fgColor rgb="FFF5F0E8"/><bgColor indexed="64"/></patternFill></fill>' +
  '</fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="5">' +
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
    '<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>' +
    '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
    '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
    '<xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '</styleSheet>'

// ── ZIP mínimo (método "store", sem compressão) ────────────
const TABELA_CRC = (() => {
  const t = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1)
    t[n] = c >>> 0
  }
  return t
})()

function crc32(bytes) {
  let c = 0xFFFFFFFF
  for (let i = 0; i < bytes.length; i++) c = TABELA_CRC[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8)
  return (c ^ 0xFFFFFFFF) >>> 0
}

function zip(ficheiros) {
  const codificador = new TextEncoder()
  const partes = []
  const central = []
  let deslocamento = 0

  const u16 = (n) => [n & 0xFF, (n >>> 8) & 0xFF]
  const u32 = (n) => [n & 0xFF, (n >>> 8) & 0xFF, (n >>> 16) & 0xFF, (n >>> 24) & 0xFF]

  for (const f of ficheiros) {
    const nome = codificador.encode(f.nome)
    const dados = codificador.encode(f.conteudo)
    const crc = crc32(dados)

    const cabecalho = [].concat(
      u32(0x04034B50), u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(dados.length), u32(dados.length), u16(nome.length), u16(0)
    )
    partes.push(new Uint8Array(cabecalho), nome, dados)

    central.push([].concat(
      u32(0x02014B50), u16(20), u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(dados.length), u32(dados.length),
      u16(nome.length), u16(0), u16(0), u16(0), u16(0), u32(0), u32(deslocamento)
    ).concat(Array.from(nome)))

    deslocamento += cabecalho.length + nome.length + dados.length
  }

  let diretorio = []
  for (const c of central) diretorio = diretorio.concat(c)

  const fim = [].concat(
    u32(0x06054B50), u16(0), u16(0), u16(ficheiros.length), u16(ficheiros.length),
    u32(diretorio.length), u32(deslocamento), u16(0)
  )

  partes.push(new Uint8Array(diretorio), new Uint8Array(fim))
  return new Blob(partes, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

// Constrói o livro. `folhas`: [{ nome, cols, linhas }]. Devolve um Blob.
export function construirXlsx(folhas) {
  const ficheiros = [
    {
      nome: '[Content_Types].xml',
      conteudo: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        folhas.map((_, i) =>
          `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
        ).join('') +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        '</Types>',
    },
    {
      nome: '_rels/.rels',
      conteudo: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    },
    {
      nome: 'xl/workbook.xml',
      conteudo: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
        'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>' +
        folhas.map((f, i) => `<sheet name="${escXml(f.nome)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
        '</sheets></workbook>',
    },
    {
      nome: 'xl/_rels/workbook.xml.rels',
      conteudo: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        folhas.map((_, i) =>
          `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`
        ).join('') +
        `<Relationship Id="rId${folhas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
        '</Relationships>',
    },
    { nome: 'xl/styles.xml', conteudo: ESTILOS },
  ]

  folhas.forEach((f, i) => {
    ficheiros.push({ nome: `xl/worksheets/sheet${i + 1}.xml`, conteudo: folhaXml(f.cols, f.linhas) })
  })

  return zip(ficheiros)
}

// Constrói e descarrega, num só passo.
export function descarregarXlsx(nomeFicheiro, folhas) {
  const url = URL.createObjectURL(construirXlsx(folhas))
  const a = document.createElement('a')
  a.href = url
  a.download = nomeFicheiro
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}
